import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { enqueueSnackbar } from 'notistack';
import {
  Box,
  Typography,
  TextField,
  Checkbox,
  FormControlLabel,
  Radio,
  RadioGroup,
  FormControl,
  FormLabel,
  CircularProgress,
  Container,
} from '@mui/material';

const FormDetails = () => {
  const { id } = useParams();
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState({});

  useEffect(() => {
    const fetchForm = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/forms/${id}`);
        setForm(response.data);
        setLoading(false);
      } catch (err) {
        setError('Failed to load form');
        setLoading(false);
        enqueueSnackbar('Failed to retrieve form details', { variant: 'error' });
      }
    };

    fetchForm();
  }, [id]);

  const handleChange = (e, fieldName) => {
    setFormData({
      ...formData,
      [fieldName]: e.target.value,
    });
  };

  const handleCheckboxChange = (e, fieldName, optionLabel) => {
    const updatedOptions = formData[fieldName] || [];
    if (e.target.checked) {
      updatedOptions.push(optionLabel);
    } else {
      const index = updatedOptions.indexOf(optionLabel);
      if (index > -1) {
        updatedOptions.splice(index, 1);
      }
    }
    setFormData({
      ...formData,
      [fieldName]: updatedOptions,
    });
  };

  const handleRadioChange = (e, fieldName) => {
    setFormData({
      ...formData,
      [fieldName]: e.target.value,
    });
  };

  if (loading) {
    return <CircularProgress />;
  }

  if (error) {
    return <Typography color="error">{error}</Typography>;
  }

  return (
    <Container maxWidth="md">
      <Box className="max-w-2xl mx-auto p-6 bg-white shadow-md rounded-md" sx={{ bgcolor: 'background.paper', boxShadow: 3, borderRadius: 2 }}>
        <Typography variant="h4" component="h2" gutterBottom sx={{ textAlign: 'center' }}>
          {form.title}
        </Typography>
        <form>
          {form.fields.map((field, index) => (
            <Box key={index} className="mb-4">
              <FormLabel component="legend" sx={{ color: 'text.primary', fontWeight: 'bold', fontSize: '1.1rem' }}>
                {field.fieldName}
              </FormLabel>
              {field.fieldType === 'checkbox' ? (
                field.options.map((option, i) => (
                  <FormControlLabel
                    key={i}
                    control={
                      <Checkbox
                        id={`${field.fieldName}-${i}`}
                        onChange={(e) => handleCheckboxChange(e, field.fieldName, option.label)}
                      />
                    }
                    label={option.label}
                  />
                ))
              ) : field.fieldType === 'radio' ? (
                <FormControl>
                  <RadioGroup
                    name={field.fieldName}
                    onChange={(e) => handleRadioChange(e, field.fieldName)}
                  >
                    {field.options.map((option, i) => (
                      <FormControlLabel
                        key={i}
                        control={<Radio id={`${field.fieldName}-${i}`} value={option.label} />}
                        label={option.label}
                      />
                    ))}
                  </RadioGroup>
                </FormControl>
              ) : (
                <TextField
                  type={field.fieldType}
                  fullWidth
                  variant="outlined"
                  onChange={(e) => handleChange(e, field.fieldName)}
                  required
                />
              )}
            </Box>
          ))}
        </form>
      </Box>
    </Container>
  );
};

export default FormDetails;
